import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, FileText } from 'lucide-react';

const TYPE_LABELS = {
  client: 'Termos de Uso',
  provider: 'Termos do Prestador',
  privacy_policy: 'Política de Privacidade',
  dispute_policy: 'Política de Contestação',
  refund_policy: 'Política de Reembolso',
};

/**
 * Modal reutilizável para exibir um documento jurídico (Termos / Política
 * de Privacidade etc.) buscando a versão ativa via `/api/admin/terms/active`.
 *
 * Props:
 *  - type: client | provider | privacy_policy | dispute_policy | refund_policy
 *  - open: bool
 *  - onOpenChange: (open) => void
 *  - testIdPrefix: string opcional para data-testid
 */
export default function LegalDocModal({ type, open, onOpenChange, testIdPrefix = 'legal-doc' }) {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [version, setVersion] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !type) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${API}/admin/terms/active`, { params: { type } });
        setContent(res.data?.content || 'Documento não disponível.');
        setVersion(res.data?.version || '');
      } catch (e) {
        setError('Não foi possível carregar o documento. Tente novamente em instantes.');
        setContent('');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, type]);

  const title = TYPE_LABELS[type] || 'Documento';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col"
        data-testid={`${testIdPrefix}-modal`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0B1F3A]">
            <FileText className="w-5 h-5" />
            {title}
          </DialogTitle>
          {version && (
            <DialogDescription data-testid={`${testIdPrefix}-version`}>
              Versão {version}
            </DialogDescription>
          )}
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800"
          style={{ whiteSpace: 'pre-wrap' }}
          data-testid={`${testIdPrefix}-content`}
        >
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Carregando...
            </div>
          )}
          {!loading && error && (
            <div className="text-red-600">{error}</div>
          )}
          {!loading && !error && content}
        </div>
      </DialogContent>
    </Dialog>
  );
}
